/**
 * BUILD-M6 — price-import status.
 *
 * The live status of one price import, fetched by id via `getPriceImport` (GET /api/price-imports/:id).
 * Reached by tapping an import on the pricing hub. Shows the current status (which changes as the
 * import is confirmed/rejected), line count, tier, and when it was created. Read-only.
 *
 * NEVER imports @smartbox/* (mobile invariant 1). Design-system primitives only; no raw style values.
 */
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useQuery } from '@tanstack/react-query';
import { getPriceImport } from '../../../../src/api/pricing';
import { Screen, Text, Card, Button, Badge, ListRow } from '../../../../src/components/ui';
import { theme, type StatusKey } from '../../../../src/theme';
import { extractErrorMessage, humanize, formatDateTime } from '../../../../src/lib/format';

function importBadge(status: string): { key: StatusKey; label: string } {
  switch (status) {
    case 'confirmed':
    case 'applied':
      return { key: 'paid', label: humanize(status) };
    case 'pending':
    case 'staged':
      return { key: 'inProgress', label: humanize(status) };
    case 'rejected':
    case 'failed':
      return { key: 'overdue', label: humanize(status) };
    default:
      return { key: 'draft', label: humanize(status) || 'Unknown' };
  }
}

export default function ImportStatusScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();

  const importQuery = useQuery({
    queryKey: ['price-import', id],
    queryFn: () => getPriceImport(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
  const imp = importQuery.data;

  if (importQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator color={theme.color.action.primary} accessibilityLabel="Loading import" />
        </View>
      </Screen>
    );
  }

  if (importQuery.isError || !imp) {
    return (
      <Screen>
        <Card>
          <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
            {extractErrorMessage(importQuery.error, 'Failed to load this price import.')}
          </Text>
          <Button title="Retry" variant="secondary" size="sm" fullWidth={false} onPress={() => importQuery.refetch()} />
        </Card>
      </Screen>
    );
  }

  const b = importBadge(imp.status);
  const lines = imp.line_count ?? 0;

  return (
    <Screen scroll>
      <View style={styles.head}>
        <Text variant="title">Price import</Text>
        <Badge status={b.key} label={b.label} />
      </View>
      <Text variant="small" color="secondary">{imp.id.slice(0, 8).toUpperCase()}</Text>

      <Card>
        <ListRow title="Status" right={<Text variant="small" color="secondary">{humanize(imp.status)}</Text>} />
        <ListRow title="Lines" right={<Text variant="small" color="secondary">{`${lines} ${lines === 1 ? 'line' : 'lines'}`}</Text>} />
        {imp.tier != null ? (
          <ListRow title="Tier" right={<Text variant="small" color="secondary">{`Tier ${imp.tier}`}</Text>} />
        ) : null}
        {imp.created_at ? (
          <ListRow title="Created" right={<Text variant="small" color="secondary">{formatDateTime(imp.created_at)}</Text>} />
        ) : null}
      </Card>
    </Screen>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  head: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing.sm },
});
